import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type KeyboardEvent,
  type RefObject,
} from 'react';

const OPEN_FOCUS_FIRST = 0;
const OPEN_FOCUS_LAST = -1;

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface UseActionsDropdownKeyboardArgs {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  triggerRef: RefObject<HTMLButtonElement | null>;
  menuRef: RefObject<HTMLDivElement | null>;
  isDisabled?: boolean;
}

export interface UseActionsDropdownKeyboardResult {
  menuId: string;
  handleTriggerKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
  handleMenuKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  closeMenu: (returnFocusToTrigger?: boolean) => void;
}

/**
 * Keyboard and focus management for ActionsDropdown's portaled menu (WAI-ARIA menu button).
 *
 * The menu renders in document.body, so native tab order does not connect the trigger
 * to its items. Tab from the last item (or Shift+Tab from the first) closes the menu
 * and moves focus back into the page sequence.
 *
 * On keyboard open, Down Arrow focuses the first item and Up Arrow the last.
 * Arrow keys and Home/End wrap between items; Escape closes the menu and returns
 * focus to the trigger.
 */
export const useActionsDropdownKeyboard = ({
  isOpen,
  setIsOpen,
  triggerRef,
  menuRef,
  isDisabled,
}: UseActionsDropdownKeyboardArgs): UseActionsDropdownKeyboardResult => {
  const menuId = useId();
  const openFocusIndexRef = useRef(OPEN_FOCUS_FIRST);

  const getMenuItems = useCallback((): HTMLButtonElement[] => {
    if (!menuRef.current) return [];

    return Array.from(
      menuRef.current.querySelectorAll<HTMLButtonElement>(
        'button:not([disabled])'
      )
    );
  }, [menuRef]);

  const focusMenuItemByIndex = useCallback(
    (index: number): void => {
      const items = getMenuItems();

      if (!items.length) return;

      const resolvedIndex =
        index === OPEN_FOCUS_LAST
          ? items.length - 1
          : Math.max(0, Math.min(index, items.length - 1));

      items[resolvedIndex]?.focus();
    },
    [getMenuItems]
  );

  const focusNextElementAfterTrigger = useCallback((): void => {
    const trigger = triggerRef.current;

    if (!trigger) return;

    const focusableElements = Array.from(
      document.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
    ).filter((element) => element.offsetParent !== null);

    const triggerIndex = focusableElements.indexOf(trigger);

    if (triggerIndex >= 0 && triggerIndex < focusableElements.length - 1) {
      focusableElements[triggerIndex + 1]?.focus();
    }
  }, [triggerRef]);

  const closeMenu = useCallback(
    (returnFocusToTrigger = false): void => {
      setIsOpen(false);

      if (returnFocusToTrigger) {
        triggerRef.current?.focus();
      }
    },
    [setIsOpen, triggerRef]
  );

  useEffect(() => {
    if (!isOpen) return undefined;

    const frameId = window.requestAnimationFrame(() => {
      focusMenuItemByIndex(openFocusIndexRef.current);
      openFocusIndexRef.current = OPEN_FOCUS_FIRST;
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [focusMenuItemByIndex, isOpen]);

  const handleTriggerKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>): void => {
      if (isDisabled) return;

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      event.preventDefault();

      if (!isOpen) {
        openFocusIndexRef.current =
          event.key === 'ArrowDown' ? OPEN_FOCUS_FIRST : OPEN_FOCUS_LAST;
        setIsOpen(true);

        return;
      }

      focusMenuItemByIndex(
        event.key === 'ArrowDown' ? OPEN_FOCUS_FIRST : OPEN_FOCUS_LAST
      );
    },
    [focusMenuItemByIndex, isDisabled, isOpen, setIsOpen]
  );

  const handleMenuKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>): void => {
      const items = getMenuItems();
      const currentIndex = items.findIndex(
        (item) => item === document.activeElement
      );

      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          closeMenu(true);
          break;
        case 'ArrowDown':
          event.preventDefault();
          if (items.length) {
            focusMenuItemByIndex(
              currentIndex < 0 ? 0 : (currentIndex + 1) % items.length
            );
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (items.length) {
            focusMenuItemByIndex(
              currentIndex <= 0 ? OPEN_FOCUS_LAST : currentIndex - 1
            );
          }
          break;
        case 'Home':
          event.preventDefault();
          focusMenuItemByIndex(0);
          break;
        case 'End':
          event.preventDefault();
          focusMenuItemByIndex(OPEN_FOCUS_LAST);
          break;
        case 'Tab':
          if (event.shiftKey && currentIndex <= 0) {
            event.preventDefault();
            closeMenu(true);
            break;
          }

          if (!event.shiftKey && currentIndex === items.length - 1) {
            event.preventDefault();
            closeMenu();
            focusNextElementAfterTrigger();
          }
          break;
        default:
          break;
      }
    },
    [
      closeMenu,
      focusMenuItemByIndex,
      focusNextElementAfterTrigger,
      getMenuItems,
    ]
  );

  return { menuId, handleTriggerKeyDown, handleMenuKeyDown, closeMenu };
};
